import { validationConstants } from "constants/formValidation";
import { orderTypeConstants } from "constants/order";
import { distributionLogicOptions } from "constants/paymentPlanConstants";
import { UserRole } from "constants/userRole";
import { useUserWithAccount } from "context/UserAccountProvider";
import { centsToDollars, dollarsToCents, parsePrice } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { getPaymentPlanEnabled } from "pages/CreateOrder/utils/helpers";
import { ISetting } from "pages/HospitalsPage/components/PaymentAndRemit/CustomPaymentPlanItem";
import { validateMinDownPaymentFlat } from "pages/OrderView/components/PaymentDetails/helpers/helper";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { useEffect, useMemo, useCallback, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { IAccountResponse } from "types/Account/AccountResponse";
import { IPaymentPlan } from "types/Payment";

import { PaymentPlanDetails } from "./PaymentPlanDetails";

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(4)};
  display: "block";
  .p-card-subtitle {
    color: var(--orange-500);
  }
  .p-card-body {
    padding-left: 0px !important;
  }
`;

// TODO: this code should be refactored among with service details component refactor
// at the moment service details component triggers multiple rerenders, because of which useEffects in this component do not work properly
// and some custom settings cases are not applied

export function PaymentPlanSection({
  accountData,
  paymentPlan,
  isRedo,
  isDraft,
}: {
  accountData: IAccountResponse;
  paymentPlan?: IPaymentPlan;
  isRedo?: boolean;
  isDraft?: boolean;
}) {
  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const { handleOnBlurField, getFormErrorMessage } = useFormValidation();
  const { userInfo } = useUserWithAccount();
  const { paymentPlanDuration, paymentPlanMinDownPaymentPercent } =
    accountData.data;
  const customPaymentPlanSettings = accountData.data.paymentPlanCustomSettings;

  const orderTypeField = watch("orderType");
  const minDownPaymentTypeField = watch("paymentPlanMinDownPaymentType");
  const minDownPaymentPercentField = watch("paymentPlanMinDownPaymentPercent");
  const amountField = watch("amount"); // for GFE
  const totalAmountField = watch("totalAmount"); // for Patient resp and default orders
  const flatAmountField = watch("paymentPlanMinDownPaymentFlat");
  const paymentPlanDurationField = watch("paymentPlanDuration");
  const paymentPlanEnabledField = watch("paymentPlanEnabled");
  const clearPatientFeePercentageField = watch("clearPatientFeePercentage"); // Clear Patient Fee percentage
  const flatAmountWithFeesField = watch("flatAmountWithFees");

  const [showMinPaymentWarning, setShowMinPaymentWarning] = useState(false);

  const paymentPlanIsEnabledForOrderType = useMemo(
    () =>
      getPaymentPlanEnabled(
        orderTypeField,
        accountData.data.paymentPlanTypeEnabled
      ),
    [orderTypeField, accountData.data.paymentPlanTypeEnabled]
  );

  const paymentPlanCustomSetting = useMemo(() => {
    if (!customPaymentPlanSettings?.length) return null;
    let amountToCompare = dollarsToCents(totalAmountField);
    if (orderTypeField === orderTypeConstants.GFE) {
      amountToCompare = dollarsToCents(amountField);
    }
    const sortedSettings = [...customPaymentPlanSettings].sort(
      (a, b) =>
        (a.min ?? Number.NEGATIVE_INFINITY) -
        (b.min ?? Number.NEGATIVE_INFINITY)
    );
    const customSetting = sortedSettings.find((setting: ISetting) => {
      const { min, max } = setting;

      if (
        min !== null &&
        min !== undefined &&
        max !== null &&
        max !== undefined
      ) {
        return amountToCompare >= min && amountToCompare <= max;
      }

      if (
        min !== null &&
        min !== undefined &&
        (max === null || max === undefined)
      ) {
        return amountToCompare >= min;
      }

      return false;
    });
    return customSetting;
  }, [amountField, totalAmountField, orderTypeField]);

  const showPercentageField = useMemo(() => {
    return minDownPaymentTypeField === "Percentage";
  }, [minDownPaymentTypeField]);

  const flatValidationAmount = useMemo(() => {
    if (orderTypeField === orderTypeConstants.GFE) {
      return dollarsToCents(amountField);
    }
    return dollarsToCents(totalAmountField);
  }, [orderTypeField, amountField, totalAmountField]);

  const resetPaymentPlanFields = useCallback(() => {
    // payment plan duration does not include first down payment, payment plan duration full includes it
    // to the user we show full duration, but in reality we do all the calculations etc. based on payment plan duration
    setValue("paymentPlanDuration", paymentPlanDuration);
    setValue("paymentPlanDurationFull", paymentPlanDuration + 1);
    setValue(
      "paymentPlanMinDownPaymentPercent",
      paymentPlanMinDownPaymentPercent
    );
    setValue("paymentPlanMinDownPaymentType", "Percentage");
    setValue("paymentPlanMinDownPaymentFlat", null);
    setValue("paymentPlanEnabled", paymentPlanIsEnabledForOrderType);
  }, [
    setValue,
    paymentPlanDuration,
    paymentPlanMinDownPaymentPercent,
    paymentPlanIsEnabledForOrderType,
  ]);

  const setCustomPaymentPlanFields = useCallback(() => {
    const enabled = paymentPlanCustomSetting?.enabled;
    setValue(
      "paymentPlanEnabled",
      paymentPlanIsEnabledForOrderType ? enabled : false
    );
    if (enabled) {
      setValue(
        "paymentPlanDuration",
        paymentPlanCustomSetting.paymentPlanDuration
      );
      setValue(
        "paymentPlanDurationFull",
        paymentPlanCustomSetting.paymentPlanDuration + 1
      );
      setValue(
        "paymentPlanMinDownPaymentType",
        paymentPlanCustomSetting.minDownPaymentType === "percent"
          ? "Percentage"
          : "Flat amount"
      );
      setValue(
        "paymentPlanMinDownPaymentFlat",
        paymentPlanCustomSetting.minDownPaymentFlat === null
          ? null
          : centsToDollars(paymentPlanCustomSetting.minDownPaymentFlat)
      );
      setValue(
        "paymentPlanMinDownPaymentPercent",
        paymentPlanCustomSetting.minDownPaymentPercent
      );
    } else {
      setValue("paymentPlanDuration", paymentPlanDuration);
      setValue("paymentPlanDurationFull", paymentPlanDuration + 1);
      setValue("paymentPlanMinDownPaymentType", "Percentage");
      setValue(
        "paymentPlanMinDownPaymentPercent",
        paymentPlanMinDownPaymentPercent
      );
      setValue("paymentPlanMinDownPaymentFlat", null);
    }
  }, [
    setValue,
    paymentPlanCustomSetting,
    paymentPlanDuration,
    paymentPlanMinDownPaymentPercent,
  ]);

  useEffect(() => {
    if (!paymentPlanCustomSetting || !paymentPlanIsEnabledForOrderType) {
      resetPaymentPlanFields();
    } else {
      setCustomPaymentPlanFields();
    }
  }, [
    paymentPlan,
    isRedo,
    isDraft,
    paymentPlanIsEnabledForOrderType,
    paymentPlanCustomSetting,
    resetPaymentPlanFields,
    setCustomPaymentPlanFields,
  ]);

  const subTitle = useMemo(() => {
    if (isRedo) {
      return "Payment Plan fields have been recalculated. Please review before submitting.";
    }
    if (isDraft) {
      return "Payment Plan fields have been recalculated. Please review before approving the order.";
    }
    return "";
  }, [isRedo, isDraft]);

  useEffect(() => {
    trigger("paymentPlanDuration");
    trigger("paymentPlanDurationFull");
    trigger("paymentPlanMinDownPaymentPercent");
    trigger("paymentPlanMinDownPaymentFlat");
    trigger("paymentPlanEnabled");
    trigger("paymentPlanMinDownPaymentType");
  }, [
    paymentPlanDurationField,
    minDownPaymentTypeField,
    minDownPaymentPercentField,
    flatAmountField,
    paymentPlanEnabledField,
  ]);

  const estimatedCost = useMemo(() => {
    if (
      !flatValidationAmount ||
      flatValidationAmount <= 0 ||
      orderTypeField !== orderTypeConstants.GFE ||
      !clearPatientFeePercentageField ||
      clearPatientFeePercentageField <= 0
    ) {
      return flatValidationAmount;
    }
    const clearPatientFeeAmount =
      flatValidationAmount * (clearPatientFeePercentageField / 100);
    const totalWithFees = flatValidationAmount + clearPatientFeeAmount;
    return totalWithFees;
  }, [flatValidationAmount, clearPatientFeePercentageField]);

  const initialPayment = useMemo(() => {
    if (minDownPaymentTypeField === "Flat amount") {
      const amount = clearPatientFeePercentageField
        ? flatAmountField * (1 + clearPatientFeePercentageField / 100)
        : flatAmountField;
      return dollarsToCents(amount);
    }
    return estimatedCost * (minDownPaymentPercentField / 100);
  }, [estimatedCost, minDownPaymentPercentField, flatAmountField]);

  const handlePaymentPlanDurationChange = useCallback(
    (e: { value: number | null }, onChange: (value: number | null) => void) => {
      setValue("paymentPlanDuration", e.value != null ? e.value - 1 : null);
      onChange(e.value);
      if (e.value) {
        setValue("paymentPlanMinDownPaymentType", "Flat amount");
        const downPayment = estimatedCost / e.value;
        if (clearPatientFeePercentageField) {
          const downGradedPayment =
            downPayment / (1 + clearPatientFeePercentageField / 100);
          setValue(
            "paymentPlanMinDownPaymentFlat",
            centsToDollars(downGradedPayment)
          );
        } else {
          setValue(
            "paymentPlanMinDownPaymentFlat",
            centsToDollars(downPayment)
          );
        }
        setValue("paymentPlanMinDownPaymentPercent", null);
      }
    },
    [setValue, clearPatientFeePercentageField, estimatedCost]
  );

  const isDownpaymentSameAsMonthlyPayment = useMemo(() => {
    const remainingAmount = estimatedCost - initialPayment;
    const monthlyPayment = centsToDollars(
      Math.floor((remainingAmount / paymentPlanDurationField) * 100) / 100
    );
    return (
      Math.abs((flatAmountWithFeesField || flatAmountField) - monthlyPayment) <=
      0.1
    );
  }, [
    flatAmountField,
    initialPayment,
    flatAmountWithFeesField,
    estimatedCost,
    paymentPlanDurationField,
  ]);

  const handleChangeMinDownPaymentType = (e: any) => {
    setValue("paymentPlanMinDownPaymentType", e.value);
    if (e.value === "Percentage") {
      setValue("paymentPlanMinDownPaymentFlat", null);
      if (
        paymentPlanCustomSetting &&
        paymentPlanCustomSetting.enabled &&
        paymentPlanCustomSetting.minDownPaymentType === "percent"
      ) {
        setValue(
          "paymentPlanMinDownPaymentPercent",
          paymentPlanCustomSetting.minDownPaymentPercent
        );
      } else {
        setValue(
          "paymentPlanMinDownPaymentPercent",
          paymentPlanMinDownPaymentPercent
        );
      }
    } else {
      setValue("paymentPlanMinDownPaymentPercent", null);
      if (
        paymentPlanCustomSetting &&
        paymentPlanCustomSetting.enabled &&
        paymentPlanCustomSetting.minDownPaymentType === "flat"
      ) {
        setValue(
          "paymentPlanMinDownPaymentFlat",
          centsToDollars(paymentPlanCustomSetting.minDownPaymentFlat || 0)
        );
      } else {
        setValue("paymentPlanMinDownPaymentFlat", null);
      }
    }
  };

  // Calculate total flat amount with clear patient fee if it exists
  const flatAmountWithFeesDisplay = useMemo(() => {
    if (
      !flatAmountField ||
      flatAmountField <= 0 ||
      orderTypeField !== orderTypeConstants.GFE ||
      !clearPatientFeePercentageField ||
      clearPatientFeePercentageField <= 0
    ) {
      return null;
    }
    // Calculate fee percentage on the flat amount only
    const clearPatientFeeAmount =
      flatAmountField * (clearPatientFeePercentageField / 100);
    const totalWithFees = flatAmountField + clearPatientFeeAmount;
    return parsePrice(totalWithFees);
  }, [flatAmountField, clearPatientFeePercentageField, orderTypeField]);

  // Set flatAmountWithFees in form data for backend submission
  useEffect(() => {
    if (flatAmountWithFeesDisplay) {
      setValue(
        "flatAmountWithFees",
        parseFloat(flatAmountWithFeesDisplay.replace(/[$,]/g, ""))
      );
    } else {
      setValue("flatAmountWithFees", null);
    }
  }, [flatAmountWithFeesDisplay, setValue]);

  if (!paymentPlan && !paymentPlanIsEnabledForOrderType) {
    return null;
  }
  if (isDraft && paymentPlan && !paymentPlanIsEnabledForOrderType) {
    return null;
  }
  return (
    <StyledCard data-testid="paymentPlan_Card" subTitle={subTitle}>
      <div className="flex flex-column gap-4 w-full">
        <div className="formgrid grid w-full">
          <div className="field flex flex-column col-12 lg:col-4 xl:col-3">
            <Label
              htmlFor="paymentPlanDurationFull"
              data-testid="paymentPlanMonthsFullLabel"
            >
              Number of payments (downpayment included)
            </Label>

            <Controller
              name="paymentPlanDurationFull"
              control={control}
              rules={{
                required: paymentPlanEnabledField
                  ? validationConstants.PAYMENT_PLAN.DURATION_REQUIRED
                  : false,
                validate: (value) =>
                  paymentPlanEnabledField
                    ? Number.isInteger(value) ||
                      validationConstants.PAYMENT_PLAN.DURATION_WHOLE_NUMBER
                    : true,
                min: {
                  value: 2,
                  message: validationConstants.PAYMENT_PLAN.DURATION_MIN,
                },
                max: {
                  value: 51,
                  message: validationConstants.PAYMENT_PLAN.DURATION_MAX,
                },
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <InputNumber
                    value={value}
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanDurationFull",
                        trigger,
                      })
                    }
                    inputId="paymentPlanDurationFull"
                    data-testid="months_PaymentPlanFull"
                    onValueChange={(e) =>
                      handlePaymentPlanDurationChange(e, onChange)
                    }
                    onChange={(e) =>
                      e.value !== null &&
                      e.value !== undefined &&
                      setShowMinPaymentWarning(e.value < 2)
                    }
                    min={2}
                    max={51}
                    placeholder="Select number of payments"
                  />
                  {showMinPaymentWarning && (
                    <p className="text-sm my-1 text-orange-500 font-medium">
                      Minimum: 2 payments (today + next installment)
                    </p>
                  )}
                  {getFormErrorMessage("paymentPlanDurationFull", errors)}
                </>
              )}
            />
          </div>
          <div className="field flex flex-column col-12 sm:col-3 lg:col-2 xl:col-1">
            <Label
              htmlFor="paymentPlanMinDownPaymentType"
              data-testid="paymentPlanMinDownPaymentType"
            >
              Type
            </Label>
            <Controller
              name="paymentPlanMinDownPaymentType"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  <Dropdown
                    id="paymentPlanMinDownPaymentType"
                    data-testid="minDownPaymentType_PaymentPlan"
                    value={value}
                    onChange={handleChangeMinDownPaymentType}
                    options={[
                      { label: "%", value: "Percentage" },
                      { label: "$", value: "Flat amount" },
                    ]}
                    placeholder="Select minimum down payment type"
                    className="w-full"
                  />
                  {minDownPaymentTypeField === "Percentage" &&
                  estimatedCost &&
                  minDownPaymentPercentField ? (
                    <p className="text-sm my-1 text-gray-600 font-normal">
                      =
                      {parsePrice(
                        centsToDollars(estimatedCost) *
                          (minDownPaymentPercentField / 100)
                      )}
                    </p>
                  ) : null}
                </>
              )}
            />
          </div>
          <div
            className={`field flex-column sm:col-6 md:col-4 lg:col-3 xl:col-3 pt-0 ${
              showPercentageField ? "flex" : "hidden"
            }`}
          >
            <Label
              htmlFor="paymentPlanMinDownPaymentPercent"
              data-testid="paymentPlanMinDownPaymentPercent"
            >
              First Payment (Today)
            </Label>

            <Controller
              name="paymentPlanMinDownPaymentPercent"
              control={control}
              rules={{
                required:
                  showPercentageField && paymentPlanEnabledField
                    ? validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_PERCENT
                    : false,
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <InputNumber
                    value={value}
                    suffix="%"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentPercent",
                        trigger,
                      })
                    }
                    inputId="paymentPlanMinDownPaymentPercent"
                    data-testid="minDownPaymentPercent_PaymentPlan"
                    onValueChange={onChange}
                    min={5}
                    max={100}
                  />
                  {getFormErrorMessage(
                    "paymentPlanMinDownPaymentPercent",
                    errors
                  )}
                </>
              )}
            />
          </div>
          <div
            className={`field  flex-column col-12 lg:col-4 xl:col-3 ${
              showPercentageField ? "hidden" : "flex"
            }`}
          >
            <Label
              htmlFor="paymentPlanMinDownPaymentFlat"
              data-testid="paymentPlanMinDownPaymentFlat"
            >
              Minimum first payment flat
            </Label>

            <Controller
              name="paymentPlanMinDownPaymentFlat"
              control={control}
              rules={{
                required:
                  !showPercentageField && paymentPlanEnabledField
                    ? validationConstants.PAYMENT_PLAN.MIN_DOWN_PAYMENT_FLAT
                    : false,
                validate: (value) =>
                  !showPercentageField && paymentPlanEnabledField
                    ? validateMinDownPaymentFlat(
                        value,
                        flatValidationAmount,
                        null
                      )
                    : true,
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <>
                  <InputNumber
                    value={value}
                    defaultValue={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "paymentPlanMinDownPaymentFlat",
                        trigger,
                      })
                    }
                    inputId="paymentPlanMinDownPaymentFlat"
                    data-testid="minDownPaymentFlat_PaymentPlan"
                    onValueChange={onChange}
                    min={10}
                    aria-autocomplete="none"
                  />
                  {flatAmountWithFeesDisplay && (
                    <div className="mt-2">
                      <p className="text-sm mb-1 text-primary">
                        with fees: {flatAmountWithFeesDisplay}
                      </p>
                    </div>
                  )}
                  {flatAmountField && (
                    <p className="text-xs mt-1 text-gray-600 font-normal">
                      {isDownpaymentSameAsMonthlyPayment
                        ? "Same as monthly payment (default)"
                        : "Custom amount"}
                    </p>
                  )}
                  {getFormErrorMessage("paymentPlanMinDownPaymentFlat", errors)}
                </>
              )}
            />
          </div>
          <div className="field flex flex-column col-12 md:col-6 lg:col-4 xl:col-3">
            <Label
              htmlFor="paymentPlanEnabled"
              data-testid="paymentPlanEnabled"
            >
              Payment Plan Enabled
            </Label>
            <Controller
              name="paymentPlanEnabled"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputSwitch checked={value} onChange={onChange} />
              )}
            />
          </div>
          {orderTypeField !== orderTypeConstants.PATIENT_RESPONSIBILITY &&
            userInfo?.scope === UserRole.SUPER_ADMIN && (
              <div className="field flex-column col-12 lg:col-4 xl:col-3">
                <Label
                  htmlFor="providerDistributionLogic"
                  data-testid="providerDistributionLogic"
                >
                  Provider Distribution Logic
                </Label>
                <Controller
                  name="providerDistributionLogic"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Dropdown
                      id="providerDistributionLogic"
                      data-testid="providerDistributionLogic_PaymentPlan"
                      value={value}
                      onChange={onChange}
                      options={Object.values(distributionLogicOptions)}
                      placeholder="Select minimum down payment type"
                      className="w-full"
                    />
                  )}
                />
              </div>
            )}
        </div>
        <PaymentPlanDetails
          accountData={accountData}
          estimatedCost={estimatedCost}
          initialPayment={initialPayment}
        />
      </div>
    </StyledCard>
  );
}
